<template>
  <component :is="props.tag" ref="reference" @click="onClick" :class="classList">
    <teleport to="body">
      <floating-transition key="floating-transition">
        <template v-if="isOpened">
          <div
            :class="popupClassList"
            ref="floating"
            :style="{
              top: `${y ?? 0}px`,
              left: `${x ?? 0}px`,
              zIndex: backdropStore.zIndex,
            }"
            :data-floating-placement="placement"
            key="floating-detached"
            @click="close"
          >
            <template v-if="$slots.popup">
              <slot name="popup"/>
            </template>
            <template v-if="props.arrow">
              <div id="floating__arrow" ref="floatingArrow"/>
            </template>
          </div>
        </template>
      </floating-transition>
    </teleport>
    <slot/>

    <backdrop
      :model-value="isOpened"
      @close="close"
    />
  </component>
</template>

<script setup>
  import FloatingTransition from './floating-transition';
  import { computed, onBeforeMount, ref, watch } from 'vue';
  import { arrow as floatingUiArrow, autoUpdate, flip, shift, useFloating } from '@floating-ui/vue';
  import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer';
  import Backdrop from '@/components/backdrop/backdrop';
  import { useBackdropStore } from '@/stores/backdrop';

  onBeforeMount(() => {
    if (typeof window !== 'undefined') {
      window.ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;
    }
  });

  const emit = defineEmits(['closed', 'opened']);

  const props = defineProps({
    tag: {
      type: String,
      default: 'div',
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
    popupClass: {
      type: null,
      default: null,
    },
    openClass: {
      type: String,
      default: 'floating--open',
    },
    placement: {
      type: null,
      default: 'bottom-start',
    },
    arrow: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
    },
    animationFrame: {
      type: Boolean,
      default: false,
    },
    hideOnClick: {
      type: Boolean,
      default: false,
    },
  });

  const reference = ref(null);
  const floating = ref(null);
  const floatingArrow = ref(null);
  const isOpened = ref(props.modelValue);
  const backdropStore = useBackdropStore();

  const { x, y, middlewareData } = useFloating(reference, floating, {
    placement: props.placement,
    middleware: [floatingUiArrow({ element: floatingArrow }), flip(), shift()],
    whileElementsMounted(...args) {
      return autoUpdate(...args, { animationFrame: props.animationFrame });
    },
  });

  watch(middlewareData, () => {
    if (middlewareData.value.arrow && floatingArrow.value) {
      const { x: arrowX, y: arrowY } = middlewareData.value.arrow;

      floatingArrow.value.style.left = arrowX === null ? '' : `${arrowX}px`;
      floatingArrow.value.style.top = arrowY === null ? '' : `${arrowY}px`;
    }
  });

  watch(() => props.modelValue, (newValue) => {
    if (newValue === false) {
      close();
    }
  });

  const classList = computed(() => {
    const list = ['floating-wrapper'];

    if (isOpened.value) {
      list.push(props.openClass);
    }

    return list;
  });

  const popupClassList = computed(() => {
    const list = ['floating'];

    if (Array.isArray(props.popupClass)) {
      return list.concat(props.popupClass);
    } else if (props.popupClass) {
      list.push(props.popupClass);
    }

    return list;
  });

  function onClick(event) {
    if (props.hideOnClick) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    toggle();
  }

  function toggle() {
    if (isOpened.value) {
      close();
    } else {
      open();
    }
  }

  function close() {
    if (!isOpened.value) {
      return;
    }

    isOpened.value = false;
    emit('closed');
  }

  function open() {
    if (isOpened.value || props.disabled) {
      return;
    }

    isOpened.value = true;
    emit('opened');
  }

  defineExpose({ floating });
</script>

